import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Chip,
  Divider,
  FormControl,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import Iconify from "src/components/iconify";
import TextField from "../../../components/form/fields/TextField";
import SelectField from "../../../components/form/fields/SelectField";
import MailTextField from "../../../components/form/fields/MailTextField";
import { anredeWithBlankArray } from "../../../components/form/fields/inputFields";
import MultipleSelectChip from "../../../components/form/fields/MultipleSelectChip";
import ControlledRadioButton from "../../../components/form/fields/ControlledRadioButton";
import ControlledCheckbox from "../../../components/form/fields/ControlledCheckbox";
import { useGetSitesQuery } from "../../../features/sites/sitesApiSlice";
import Loading from "../../../components/spinners/Loading";

function NewUserForm({ initialValues, onSubmit, dialogType }) {
  const { data: sites, isLoading, isFetching } = useGetSitesQuery();

  const siteIds = initialValues?.sites?.map((site) => site._id) || [];

  const determineUserRole = (role) => {
    if (role?.Admin) {
      return "isAdmin";
    } else if (role?.Editor) {
      return "isEditor";
    } else if (role?.User) {
      return "isUser";
    } else if (role?.Mitglied) {
      return "isMember";
    }
    return "isUser";
  };
  const { handleSubmit, control, watch } = useForm({
    defaultValues: {
      ...initialValues,
      roles: determineUserRole(initialValues?.roles),
      sites: siteIds,
      canManageProducts: Boolean(
        initialValues?.shopPermissions?.canManageProducts,
      ),
    },
  });

  const permissionItems = [
    {
      value: "isUser",
      label: "Trainer",
      helperText: "Lesezugriff",
    },
    {
      value: "isEditor",
      label: "Editor",
      helperText: "Mitglieder erstellen und bearbeiten",
    },
    {
      value: "isAdmin",
      label: "Admin",
      helperText: "Voller Zugriff, Mitgliederverwaltung und Banking.",
    },
  ];

  if (dialogType === "EDIT") {
    permissionItems.unshift({
      value: "isMember",
      label: "Mitglied",
      helperText: "Nur Mitgliedsportal-Zugriff ohne Trainer-/Admin-Rechte.",
    });
  }

  const selectedRole = watch("roles");

  if (isLoading || isFetching) {
    return <Loading />;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} id="user-form" className="form">
      <Stack spacing={3}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6">Konto</Typography>
              <Typography variant="body2" color="text.secondary">
                Gib hier die E-Mail-Adresse des Benutzers ein. Diese wird als
                Login verwendet.
              </Typography>
            </Box>
            <Divider />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <MailTextField control={control} required={true} fullWidth />
              </Grid>
              {dialogType !== "EDIT" && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Nach dem Speichern wird eine Aktivierungs-E-Mail versendet.
                    Der Benutzer legt sein Passwort selbst fest.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6">Persönliche Daten</Typography>
              <Typography variant="body2" color="text.secondary">
                Diese Angaben werden im System angezeigt.
              </Typography>
            </Box>
            <Divider />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4} md={3}>
                <SelectField
                  control={control}
                  name="title"
                  label="Anrede"
                  variant="outlined"
                  itemArray={anredeWithBlankArray}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <TextField
                  name="firstName"
                  label="Vorname"
                  errorMsg="Bitte angeben"
                  control={control}
                  required={true}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4} md={5}>
                <TextField
                  name="lastName"
                  label="Nachname"
                  errorMsg="Bitte angeben"
                  control={control}
                  required={true}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="h6">Zugriff & Rollen</Typography>
                <Typography variant="body2" color="text.secondary">
                  Lege fest, welche Bereiche der Benutzer sehen und bearbeiten
                  darf.
                </Typography>
              </Box>
              <Chip
                label={
                  permissionItems.find((item) => item.value === selectedRole)
                    ?.label || "Benutzer"
                }
                color="primary"
                variant="contained"
              />
            </Box>
            <Divider />
            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <FormControl component="fieldset" variant="standard">
                  <FormLabel component="legend">Standorte zuweisen</FormLabel>
                  <FormGroup>
                    <MultipleSelectChip
                      name="sites"
                      label="Zugeordnete Standorte"
                      errorMsg="Bitte angeben"
                      required={true}
                      itemArray={
                        sites &&
                        sites.map((o) => ({
                          label: o.siteName,
                          value: o._id,
                        }))
                      }
                      control={control}
                    />
                  </FormGroup>
                  <FormHelperText>
                    Bitte weisen Sie dem Nutzer mindestens einen Standort zu.
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={5}>
                <FormControl component="fieldset" variant="standard">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <FormLabel component="legend">
                      Benutzerrolle auswählen
                    </FormLabel>
                    <Tooltip
                      title={
                        <Box sx={{ maxWidth: 300 }}>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: "bold", marginBottom: 1 }}
                          >
                            Rechte:
                          </Typography>
                          <ul style={{ paddingLeft: 20, margin: 0 }}>
                            {permissionItems.map((item) => (
                              <li key={item.value}>
                                <Typography variant="body2">
                                  <strong>{item.label}:</strong>{" "}
                                  {item.helperText}
                                </Typography>
                              </li>
                            ))}
                          </ul>
                        </Box>
                      }
                      placement="left"
                      arrow
                    >
                      <Iconify
                        icon="clarity:help-solid"
                        style={{ fontSize: "20px", color: "action.active" }}
                        sx={{
                          transition: "transform 0.2s",
                          "&:hover": {
                            transform: "scale(1.1)",
                          },
                        }}
                      />
                    </Tooltip>
                  </Box>
                  <ControlledRadioButton
                    control={control}
                    name="roles"
                    itemArray={permissionItems.map((item) => ({
                      value: item.value,
                      label: item.label,
                    }))}
                    // required={true}
                  />
                  <FormHelperText>
                    {permissionItems.find((item) => item.value === selectedRole)
                      ?.helperText ||
                      "Wählen Sie eine Rolle aus, um Details zu sehen."}
                  </FormHelperText>
                </FormControl>
                <Box sx={{ mt: 2 }}>
                  <ControlledCheckbox
                    control={control}
                    name="canManageProducts"
                    label="Shop: Produkt-CRUD erlauben"
                  />
                  <FormHelperText>
                    Admin hat diese Berechtigung immer. Für andere Benutzer kann
                    sie hier explizit freigegeben werden.
                  </FormHelperText>
                </Box>
              </Grid>
            </Grid>
          </Stack>
        </Paper>
      </Stack>
    </form>
  );
}

NewUserForm.propTypes = {
  initialValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
};

export default NewUserForm;
